import type { FloatingContext } from "@floating-ui/react";
import React, { useRef } from "react";
import { createPortal } from "react-dom";
import { VisibilityProvider } from "../Visibility/Visibility";

export interface PopoverContextValue extends Omit<FloatingContext, "open" | "onOpenChange"> {
  getTriggerProps?: () => Record<string, unknown>;
  getFloatingProps?: () => Record<string, unknown>;
  visible: boolean;
  setVisible: (value: boolean) => void;
  preRendered: boolean;
  prerender: () => void;
  tags: string[];
  interactable: boolean;
}

const PopoverContext = React.createContext<PopoverContextValue | undefined>(undefined);

export const usePopover = () => {
  const popoverContext = React.useContext(PopoverContext);
  return popoverContext;
};

let globalPopoverContainer: HTMLDivElement | null = null;

export const usePopoverFloating = (options?: { portal?: boolean }) => {
  const popoverContext = React.useContext(PopoverContext);

  const everVisible = useRef(false);

  if (!popoverContext) {
    return null;
  }

  if ((popoverContext.visible || popoverContext.preRendered) && !everVisible.current) everVisible.current = true;

  return {
    context: popoverContext,
    props: {
      ref: popoverContext.refs.setFloating,
      style: popoverContext.floatingStyles,
      "data-popover-visible": popoverContext.visible,
      ...popoverContext.getFloatingProps?.(),
    },
    render: (children: React.ReactNode) => {
      const referenceElement = popoverContext.refs.reference.current;
      const isInsideDialog =
        typeof Element !== "undefined" &&
        referenceElement instanceof Element &&
        Boolean(referenceElement.closest("[role='dialog'], [data-deci-modal='true']"));
      const shouldPortal = options?.portal ?? !isInsideDialog;
      const popoverContainer =
        typeof document === "undefined"
          ? null
          : globalPopoverContainer ||
            (globalPopoverContainer = document.querySelector("[data-popover-container]")) ||
            document.body;

      return (
        <VisibilityProvider value={{ visible: popoverContext.visible }}>
          {everVisible.current
            ? shouldPortal
              ? popoverContainer
                ? createPortal(children, popoverContainer)
                : null
              : children
            : null}
        </VisibilityProvider>
      );
    },
  };
};

export default PopoverContext;
